import { html, TemplateResult } from 'lit';
import { AnalyticsDashboardData } from '../../types/analytics';
import { iconPixelArrowLeft, iconPixelEye, iconPixelEyeOff } from '../../icons';
import '../app-button/app-button';

export interface AnalyticsViewProps {
  isUnlocked: boolean;
  isLoading: boolean;
  passwordInput: string;
  showPassword?: boolean;
  hasError: boolean;
  errorMessage?: string;
  data: AnalyticsDashboardData | null;
  onPasswordInput: (value: string) => void;
  onToggleShowPassword?: () => void;
  onUnlockSubmit: () => void;
  onBackToHome: () => void;
}

function timeAgo(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const seconds = Math.round((now.getTime() - date.getTime()) / 1000);

  if (seconds < 60) return 'just now';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  return `${days}d ago`;
}

export function renderAnalyticsViewPresentation(props: AnalyticsViewProps): TemplateResult {
  const { showPassword = false } = props;

  if (!props.isUnlocked) {
    return html`
      <div class="password-modal-overlay">
        <div class="password-modal-card">
          <div class="modal-top-bar">
            <app-button
              variant="text"
              label="Back"
              @click=${props.onBackToHome}
            >
              <span slot="icon-left" style="margin-right: 0.2rem;">${iconPixelArrowLeft(12)}</span>
            </app-button>
          </div>
          <h2 class="modal-title">Analytics</h2>
          <p class="modal-subtitle">Protected Dashboard</p>
          
          <form
            class="password-form"
            @submit=${(e: Event) => {
              e.preventDefault();
              props.onUnlockSubmit();
            }}
          >
            <div class="input-row">
              <div class="password-input-wrapper">
                <input
                  type=${showPassword ? 'text' : 'password'}
                  class="password-input"
                  placeholder="Enter password..."
                  .value=${props.passwordInput}
                  @input=${(e: Event) => props.onPasswordInput((e.target as HTMLInputElement).value)}
                  autofocus
                />
                ${props.onToggleShowPassword
                  ? html`
                      <button
                        type="button"
                        class="toggle-password-btn"
                        title=${showPassword ? 'Hide password' : 'Show password'}
                        @click=${props.onToggleShowPassword}
                      >
                        ${showPassword ? iconPixelEyeOff(14) : iconPixelEye(14)}
                      </button>
                    `
                  : ''}
              </div>
              <app-button
                variant="primary"
                label="Unlock"
                @click=${props.onUnlockSubmit}
                ?disabled=${props.isLoading}
              ></app-button>
            </div>
            ${props.hasError
              ? html`<div class="error-msg">${props.errorMessage || 'Incorrect password. Please try again.'}</div>`
              : ''}
          </form>
        </div>
      </div>
    `;
  }

  if (props.isLoading || !props.data) {
    return html`
      <div class="loading-container">
        Loading analytics...
      </div>
    `;
  }

  const { data } = props;

  return html`
    <app-button
      variant="text"
      label="Back"
      @click=${props.onBackToHome}
    >
      <span slot="icon-left" style="margin-right: 0.2rem;">${iconPixelArrowLeft(12)}</span>
    </app-button>
    
    <h1 style="font-family: var(--font-serif, serif); font-size: 2.1rem; margin-top: 1rem; margin-bottom: 1.5rem;">Analytics Dashboard</h1>

    <div class="stat-grid">
      <div class="stat-card">
        <div class="stat-value">${data.totalViews.toLocaleString()}</div>
        <div class="stat-label">Total Views</div>
      </div>
      <div class="stat-card">
        <div class="stat-value">${data.last7DaysViews.toLocaleString()}</div>
        <div class="stat-label">Last 7 Days</div>
      </div>
      <div class="stat-card">
        <div class="stat-value">${data.last30DaysViews.toLocaleString()}</div>
        <div class="stat-label">Last 30 Days</div>
      </div>
    </div>

    <h2 class="section-title">Per-Post Views</h2>
    <div class="table-wrapper">
      <table>
        <thead>
          <tr>
            <th>Post Title</th>
            <th>Views</th>
            <th>% of Total</th>
          </tr>
        </thead>
        <tbody>
          ${data.perPost.map(post => html`
            <tr>
              <td>${post.title || post.path}</td>
              <td>${post.views.toLocaleString()}</td>
              <td>
                ${post.percentage.toFixed(1)}%
                <div class="bar-container">
                  <div class="bar-fill" style="width: ${post.percentage}%"></div>
                </div>
              </td>
            </tr>
          `)}
        </tbody>
      </table>
    </div>

    <h2 class="section-title">Device Breakdown</h2>
    <div class="stat-grid">
      ${Object.entries(data.deviceBreakdown).map(([device, count]) => html`
        <div class="stat-card">
          <div class="stat-value">${count.toLocaleString()}</div>
          <div class="stat-label">${device}</div>
        </div>
      `)}
    </div>

    <h2 class="section-title">Geographic Distribution</h2>
    <div class="table-wrapper">
      <table>
        <thead>
          <tr>
            <th>Region</th>
            <th>Timezone</th>
            <th>Views</th>
          </tr>
        </thead>
        <tbody>
          ${data.geographicBreakdown.map(entry => html`
            <tr>
              <td>${entry.region}</td>
              <td>${entry.timezone}</td>
              <td>${entry.count.toLocaleString()}</td>
            </tr>
          `)}
        </tbody>
      </table>
    </div>

    <h2 class="section-title">Top Referrers</h2>
    <div class="table-wrapper">
      <table>
        <thead>
          <tr>
            <th>Source</th>
            <th>Views</th>
          </tr>
        </thead>
        <tbody>
          ${data.topReferrers.map(ref => html`
            <tr>
              <td>${ref.domain}</td>
              <td>${ref.count.toLocaleString()}</td>
            </tr>
          `)}
        </tbody>
      </table>
    </div>

    <h2 class="section-title">Recent Activity</h2>
    <div class="table-wrapper">
      <table>
        <thead>
          <tr>
            <th>Page</th>
            <th>Device</th>
            <th>Location</th>
            <th>Time</th>
          </tr>
        </thead>
        <tbody>
          ${data.recentActivity.map(activity => html`
            <tr>
              <td>${activity.title || activity.path}</td>
              <td>${activity.deviceType}</td>
              <td>${activity.timeZone}</td>
              <td>${timeAgo(activity.timestamp)}</td>
            </tr>
          `)}
        </tbody>
      </table>
    </div>
  `;
}
